/**
 * 承認詳細ダイアログ
 *
 * 承認依頼の詳細表示と承認・却下アクションを行うダイアログ
 * pending_data の original_values / new_values を「変更前」「変更後」として
 * 統一的に表示し、案件詳細画面やDB値との不一致を解消する。
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { ApprovalRequest } from '../../services/salesApi';
import { formatDateTimeJST } from '../../utils/dateUtils';

interface ApprovalDetailDialogProps {
  open: boolean;
  onClose: () => void;
  request: ApprovalRequest | null;
  onApprove: (caseId: number) => Promise<void>;
  onReject: (caseId: number, comment: string) => Promise<void>;
  isApprover: boolean;
}

const ApprovalDetailDialog: React.FC<ApprovalDetailDialogProps> = ({
  open,
  onClose,
  request,
  onApprove,
  onReject,
  isApprover,
}) => {
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleApprove = async () => {
    if (!request) return;
    setProcessing(true);
    setError('');
    try {
      await onApprove(request.case_id);
      handleClose();
    } catch (err: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      setError(err.response?.data?.message || '承認処理に失敗しました');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!request) return;
    if (!rejectComment.trim()) {
      setError('却下理由を入力してください');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      await onReject(request.case_id, rejectComment);
      handleClose();
    } catch (err: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      setError(err.response?.data?.message || '却下処理に失敗しました');
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setRejectComment('');
    setShowRejectForm(false);
    setError('');
    onClose();
  };

  const getRequestTypeLabel = (type?: string) => {
    switch (type) {
      case 'phase_change': return 'フェーズ変更';
      case 'resource_change': return '要員配置・金額変更';
      case 'phase_and_amount_change': return 'フェーズ・金額変更';
      case 'initial': return '新規案件登録';
      default: return '変更';
    }
  };

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return `¥${amount.toLocaleString()}`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return formatDateTimeJST(dateStr, true);
  };

  const pendingAmounts = useMemo(() => {
    if (!request) return { sales: 0, grossProfit: 0 };
    if (request.pending_amount !== undefined) {
      return { sales: request.pending_amount, grossProfit: request.pending_gross_profit || 0 };
    }
    const allocations = request.pending_data?.resource_allocations || [];
    let totalSales = 0;
    let totalCost = 0;
    for (const alloc of allocations) {
      const unitPrice = parseFloat(String(alloc.unit_price)) || 0;
      const costRate = parseFloat(String(alloc.cost_rate)) || 0;
      if (!alloc.start_month || !alloc.end_month) continue;
      const start = new Date(alloc.start_month);
      const end = new Date(alloc.end_month);
      const months = (end.getFullYear() * 12 + end.getMonth()) - (start.getFullYear() * 12 + start.getMonth()) + 1;
      totalSales += unitPrice * months;
      totalCost += costRate * months;
    }
    return { sales: totalSales, grossProfit: totalSales - totalCost };
  }, [request]);

  if (!request) return null;

  const pendingData = request.pending_data || {};
  const isInitial = pendingData.request_type === 'initial';
  const isPhaseChange = pendingData.request_type === 'phase_change' || pendingData.request_type === 'phase_and_amount_change';
  const isAmountChange = pendingData.request_type === 'resource_change' || pendingData.request_type === 'phase_and_amount_change';
  const originalValues = pendingData.original_values || {};
  const newValues = pendingData.new_values || {};

  const beforePhaseName = originalValues.sales_phase_name || request.current_phase_name || '-';
  const afterPhaseName = newValues.sales_phase_name || request.pending_phase_name || beforePhaseName;
  const beforeAmount = request.current_amount || originalValues.expected_order_amount || 0;
  const afterAmount = pendingAmounts.sales;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
        承認依頼詳細
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 基本情報 */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">案件番号</Typography>
              <Typography>{request.case_no}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">取引先</Typography>
              <Typography>{request.account_name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">申請者</Typography>
              <Typography>{request.requester_name || '-'}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6">
              {isInitial ? '新規登録内容' : '変更内容比較'}
            </Typography>
            <Chip
              label={getRequestTypeLabel(pendingData.request_type)}
              size="small"
              color="primary"
            />
          </Box>

          {/* 新規案件 */}
          {isInitial ? (
            <Box sx={{ bgcolor: 'primary.50', p: 2, borderRadius: 1, mb: 2, border: '1px solid', borderColor: 'primary.200' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">案件名</Typography>
                  <Typography fontWeight="bold">{newValues.case_name || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">取引先</Typography>
                  <Typography>{newValues.account_name || request.account_name || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">営業フェーズ</Typography>
                  <Typography color="primary" fontWeight="bold">{afterPhaseName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">事業カテゴリ</Typography>
                  <Typography>{newValues.business_category || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">受注予定額</Typography>
                  <Typography color="primary" fontWeight="bold">{formatAmount(afterAmount)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">粗利額</Typography>
                  <Typography color="primary" fontWeight="bold">{formatAmount(pendingAmounts.grossProfit)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">営業担当者</Typography>
                  <Typography>{newValues.assigned_employee_name || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">要員数</Typography>
                  <Typography>{pendingData.resource_allocations?.length ?? newValues.resource_count ?? 0}名</Typography>
                </Grid>
              </Grid>
            </Box>
          ) : (
            /* 既存案件の変更比較テーブル */
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>項目</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>変更前（DB保存済み）</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '35%', color: 'primary.main' }}>変更後（申請内容）</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* 案件名 */}
                  {newValues.case_name && newValues.case_name !== originalValues.case_name && (
                    <TableRow>
                      <TableCell>案件名</TableCell>
                      <TableCell>{originalValues.case_name || request.case_name || '-'}</TableCell>
                      <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>{newValues.case_name}</TableCell>
                    </TableRow>
                  )}
                  {/* フェーズ */}
                  {isPhaseChange && (
                    <TableRow>
                      <TableCell>営業フェーズ</TableCell>
                      <TableCell>{beforePhaseName}</TableCell>
                      <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>{afterPhaseName}</TableCell>
                    </TableRow>
                  )}
                  {/* 金額 */}
                  {isAmountChange && (
                    <>
                      <TableRow>
                        <TableCell>受注予定額</TableCell>
                        <TableCell>{formatAmount(beforeAmount)}</TableCell>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>{formatAmount(afterAmount)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>粗利額</TableCell>
                        <TableCell>{formatAmount(request.current_gross_profit || originalValues.gross_profit)}</TableCell>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>{formatAmount(pendingAmounts.grossProfit)}</TableCell>
                      </TableRow>
                    </>
                  )}
                  {/* 担当者 */}
                  {newValues.assigned_employee_id && newValues.assigned_employee_id !== originalValues.assigned_employee_id && (
                    <TableRow>
                      <TableCell>営業担当者</TableCell>
                      <TableCell>{originalValues.assigned_employee_name || '-'}</TableCell>
                      <TableCell sx={{ color: 'warning.main', fontWeight: 'bold' }}>{newValues.assigned_employee_name || '-'}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* 要員計画の変更比較 */}
          {((pendingData.resource_allocations?.length ?? 0) > 0 || (pendingData.original_allocations?.length ?? 0) > 0) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                要員計画の変更
              </Typography>
              <Grid container spacing={2}>
                {pendingData.original_allocations && pendingData.original_allocations.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, height: '100%' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                        変更前（{pendingData.original_allocations.length}名）
                      </Typography>
                      {pendingData.original_allocations.map((alloc: any, index: number) => ( // eslint-disable-line @typescript-eslint/no-explicit-any
                        <Box key={index} sx={{ p: 0.5, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {alloc.resource_name || '未定'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            単価: {formatAmount(alloc.unit_price)} / {alloc.start_month?.substring(0, 7)} ～ {alloc.end_month?.substring(0, 7)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sm={(pendingData.original_allocations?.length ?? 0) > 0 ? 6 : 12}>
                  <Box sx={{ bgcolor: 'primary.50', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'primary.200', height: '100%' }}>
                    <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                      変更後（{pendingData.resource_allocations?.length || 0}名）
                    </Typography>
                    {pendingData.resource_allocations?.map((alloc: any, index: number) => ( // eslint-disable-line @typescript-eslint/no-explicit-any
                      <Box key={index} sx={{ p: 0.5, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                          {alloc.resource_name || '未定'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          単価: {formatAmount(alloc.unit_price)} / {alloc.start_month?.substring(0, 7)} ～ {alloc.end_month?.substring(0, 7)}
                        </Typography>
                      </Box>
                    ))}
                    {(!pendingData.resource_allocations || pendingData.resource_allocations.length === 0) && (
                      <Typography variant="body2" color="text.secondary">要員なし</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* 変更理由 */}
          {pendingData.change_reason && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">変更理由</Typography>
              <Typography>{pendingData.change_reason}</Typography>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">申請日時</Typography>
            <Typography>{formatDate(request.approval_requested_at)}</Typography>
          </Box>

          {/* 却下フォーム */}
          {showRejectForm && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                却下理由を入力してください *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="却下理由を入力"
                error={!rejectComment.trim() && error !== ''}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={processing}>
          閉じる
        </Button>
        {isApprover && (request.approval_status === 'pending' || request.approval_status === 'new_pending') && (
          <>
            {showRejectForm ? (
              <>
                <Button onClick={() => setShowRejectForm(false)} disabled={processing}>
                  戻る
                </Button>
                <Button
                  onClick={handleReject}
                  variant="contained"
                  color="error"
                  disabled={processing || !rejectComment.trim()}
                >
                  {processing ? <CircularProgress size={24} /> : '却下する'}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => setShowRejectForm(true)} color="error" disabled={processing}>
                  却下
                </Button>
                <Button
                  onClick={handleApprove}
                  variant="contained"
                  color="success"
                  disabled={processing}
                >
                  {processing ? <CircularProgress size={24} /> : '承認する'}
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalDetailDialog;
